import { Form, Formik } from "formik";
import React, { FC, ReactElement, useEffect, useState } from "react";
import Modal from 'react-modal';
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ENROLL_TYPE } from "../../../constants/command-status";
import { STANDARD_NUMBER_OF_BITS } from "../../../constants/credential-constants";
import settingTypesBasicFields from "../../../constants/setting-types-basic-fields";
import { EnrolSubCmds } from "../../../constants/sub-commands";
import TagStatus from '../../../constants/tag-status';
import { TITLE_BKGCOLOR, TITLE_FGCOLOR } from "../../../constants/widget-constants";
import { WS_URL } from "../../../constants/ws-constants";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useDashboardContext } from "../../../contexts/dashboard/DashboardContext";
import { useLocale } from "../../../contexts/localeContext";
import quickEnrollIcon from "../../../images/Quick Enroll.svg";
import { ButtonTypes } from "../../../models/ButtonTypes";
import { CategoryFieldGroup } from "../../../models/CategoryFieldGroup";
import { EnrolmentDevice } from "../../../models/EnrolmentDevices";
import { EnrolmentTask } from "../../../models/EnrolmentTask";
import { ExecuteCommand } from "../../../models/ExecuteCommand";
import { Person } from "../../../models/Person";
import { PluginCommand } from "../../../models/PluginCommand";
import { SettingData } from "../../../models/SettingData";
import { Tag } from "../../../models/Tag";
import { Widget } from "../../../models/Widget";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { getAccessGroupsForPerson } from "../../../services/accessGroupService";
import { saveCredential } from "../../../services/credentialsService";
import { storePerson } from "../../../services/enrolmentService";
import { requestEnrolDevice } from "../../../services/executeCommandService";
import { assignUnassignMasterGroup } from "../../../services/masterGroupService";
import { saveSettingData } from "../../../services/settingDataService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import enrollCmd from "../../../services/wsPluginService/plugins/base/enrollCmd";
import { IShowEnrollValidOptions } from "../../../services/wsPluginService/plugins/base/pluginModels";
import { IPluginCard, IPluginCards } from "../../../services/wsPluginService/plugins/card/cardModels";
import { decodeMDEPluginMsg } from "../../../services/wsPluginService/plugins/card/mdePlugin";
import { decodeOmnikeyPluginMsg } from "../../../services/wsPluginService/plugins/card/omnikeyPlugin";
import { checkPluginValidatedList, getEnrollResultCommandType, isPluginValidationListInterface } from "../../../services/wsPluginService/plugins/decodePluginMsg";
import { validatePluginMsg } from "../../../services/wsPluginService/plugins/validatePluginMsg";
import WSPluginService from "../../../services/wsPluginService/wsPluginService";
import { IAppState } from "../../../store/types";
import { appendFilter } from "../../../utilities/filters";
import { DialogButton, DialogContent } from "../../dialog/Dialog";
import { useDialog } from "../../dialog/DialogContext";
import DetailsForm from "../../enroll-credentials/details-form";
import SubTasksForm from "../../enroll-credentials/sub-tasks-form";
import TasksForm from "../../enroll-credentials/tasks-form";
import WizardTwo from "../../wizard/wizardTwo";
import BaseWidget from "../base-widget";
import ColorStep from "../color-step";
import EditWidgetModal from "../edit-wiget-modal";
import QuickEnrolSummary from "./add/summary";
import TypeSelectionStep from "./add/type-selection-step";
import UserDetailsStep from "./add/user-details-step";
import SummaryStep from "./summary-step";
import GroupStep from "./add/group-step";
import { getTagTypes } from "@/constants/tag-type";

interface QuickEnrolWidgetProps {
    widget: Widget;
    previewMode?: boolean;
}

const QuickEnrolWidget: FC<QuickEnrolWidgetProps> = ({ widget, previewMode = false }) => {
    const { translate } = useLocale();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<number>(0);
    const { editMode } = useDashboardContext();
    const [supportedFields, setSupportedFields] = useState<CategoryFieldGroup[]>([]);
    const initialSteps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.type),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.userDetails),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
    ];
    const [availableSystemDeviceIds, setAvailableSystemDeviceIds] = useState<number[]>([]);
    const defaultPluginsAvailable: IShowEnrollValidOptions = { mde: false, camera: false, print: false, omnikey: false, morpho: false, suprema: false, nitgen: false, extension: false, lumidigm: false, controlid: false };
    const [pluginsAvailable, setPluginsAvailable] = useState<IShowEnrollValidOptions>(defaultPluginsAvailable);
    const [pluginEnrolMsg, setPluginEnrolMsg] = useState<string>(''); // for plugin pluginWSInstance messages
    const [pluginWSInstance, setPluginWSInstance] = useState<WebSocket | undefined>();
    const enrolmentTask = new EnrolmentTask(translate);
    const initialValues: any = {
        id: 0,
        code: '',
        tagDisplay: '',
        type: '',
        typeId: "0",
        codeUntruncated: '',
        startDate: undefined,
        startTime: '',
        expiryDate: undefined,
        expiryTime: '',
        statusId: '1',
        firstName: '',
        lastName: '',
        name: '',
        tagOss: undefined
    };
    const [sideBarSteps, setSideBarSteps] = useState<WizardSideBarStep[]>(initialSteps);
    const [accessGroupField, setAccessGroupField] = useState<boolean>(false)
    const [tagsField, setTagsField] = useState<boolean>(false)
    const [cardValues, setCardValues] = useState<Tag>(initialValues);
    const [taskProcedure, setTaskProcedure] = useState<string>("");
    const [credentialType, setCredentialType] = useState<string>("");
    const enrolmentDevice = new EnrolmentDevice(translate);
    const dialog = useDialog();
    const [dualCard, setDualCard] = useState<any>(undefined);
    const [skip, setSkip] = useState<boolean>(false);
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [isOpen, setIsOpen] = useState(false);
    const dashboard = useDashboardContext();

    useEffect(() => {
        // is there actually a valid message here
        if (typeof pluginEnrolMsg === "string" && pluginEnrolMsg.length > 5) {

            // make sure we have an instance and the ws is open
            if (pluginWSInstance !== undefined && pluginWSInstance.readyState === WebSocket.OPEN) {
                // send it!
                pluginWSInstance.send(pluginEnrolMsg);
            }
        }

        // it has text, therefore set to '' again
        if (pluginEnrolMsg !== undefined) {
            setPluginEnrolMsg('');
        }

    }, [pluginEnrolMsg]);

    const closeWizard = () => {
        pluginWSInstance?.close(1000, "Closing view");
        setAvailableSystemDeviceIds([]);
        setPluginsAvailable(defaultPluginsAvailable);
        handleClose();
    }

    const onWebSocketError = (error) => {
        console.log('Plugin Manager not detected. Some enrolment capabilities will be unavailable.');
        setPluginWSInstance(undefined);
    };

    const onWebSocketClosed = (e) => {
        setPluginWSInstance(undefined);
    };

    const onWebSocketOpen = (instance, value) => {
        // set the instance so we can use anywhere here
        setPluginWSInstance(instance);
        // set the validation message to send first so we can get a list of validate plugins
        setPluginEnrolMsg(validatePluginMsg());
    };

    const onMessageReceived = (value) => {
        if (value !== undefined && isPluginValidationListInterface(value)) {
            // validation message
            const validPlugins = checkPluginValidatedList(value) as IShowEnrollValidOptions;
            setPluginsAvailable(validPlugins);
        }
    };

    useEffect(() => {
        const command: ExecuteCommand = {
            cmd: ENROLL_TYPE.ENROLL,
            cmdSubType: EnrolSubCmds.Enroll_Devices_Available
        }

        requestEnrolDevice(command).then((response) => {
            setAvailableSystemDeviceIds(response.ids);
        }).catch(() => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.availableSystemDeviceFailure) });
            setAvailableSystemDeviceIds([]);
        });
    }, [open === true])

    // Handle Plugin Manager messages
    const onMessage = (value) => {
        if (value !== undefined) {
            if (getEnrollResultCommandType(value) === enrollCmd.CMD_MDE) {
                const mdeCards = decodeMDEPluginMsg(value) as IPluginCards;

                // Card details available
                if (!!mdeCards && !!mdeCards.cards && mdeCards.cards.length > 0) {

                    // Dual Card: user to choose between High and Low card to enroll
                    if (mdeCards.cards[0].cardNumber && mdeCards.cards[1].cardNumber) {
                        setDualCard(mdeCards);

                        // Buttons to show in Dual Card toast
                        const dialogButtons: DialogButton[] = [
                            {
                                id: "btn_high",
                                onClick: () => { showDualCardPortion(mdeCards.cards[1]) },
                                text: getCardTypeLabel(mdeCards.cards[1].cardType),
                                type: ButtonTypes.BlueRoundedButton
                            },
                            {
                                id: "btn_low",
                                onClick: () => { showDualCardPortion(mdeCards.cards[0]) }, // Empty onClick will just close the dialog
                                text: getCardTypeLabel(mdeCards.cards[0].cardType),
                                type: ButtonTypes.GreenRoundedButton,
                                className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                            }
                        ];

                        // Show the dual card message, ask user to choose which card to enroll
                        const lowCard = getCardTypeLabel(mdeCards.cards[0].cardType) + ': '+mdeCards.cards[0].cardNumber;
                        const highCard = getCardTypeLabel(mdeCards.cards[1].cardType) + ': '+mdeCards.cards[1].cardNumber;
                        const toastMessage = translate.byKeyFormatted(TranslationKeys.dualCardDetected, [lowCard, highCard]);
                        const dialogContent: DialogContent = {
                            title: translate.byKey(TranslationKeys.dualCard),
                            message: toastMessage,
                            buttons: dialogButtons
                        }
                        dialog.pushInfo(dialogContent);
                    }
                    // Low card found
                    else if (mdeCards.cards[0].cardNumber !== undefined) {
                        setCardValues({
                            ...cardValues,
                            code: mdeCards.cards[0].cardNumber,
                            tagDisplay: mdeCards.cards[0].cardNumber,
                            type: getCardTypeLabel(mdeCards.cards[0].cardType),
                            typeId: getCardTypeId(mdeCards.cards[0].cardType)
                        }) // for now just select first one
                    }
                    // High card found
                    else if (mdeCards.cards[1].cardNumber !== undefined) {
                        setCardValues({
                            ...cardValues,
                            code: mdeCards.cards[1].cardNumber,
                            tagDisplay: mdeCards.cards[1].cardNumber,
                            type: getCardTypeLabel(mdeCards.cards[1].cardType),
                            typeId: getCardTypeId(mdeCards.cards[1].cardType)
                        })
                    }
                    // No card number found
                    else {
                        if (mdeCards.cards[0].cardNumber !== undefined) {
                            setCardValues({
                                ...cardValues,
                                code: mdeCards.cards[0].cardNumber,
                                tagDisplay: mdeCards.cards[0].cardNumber,
                                type: getCardTypeLabel(mdeCards.cards[0].cardType),
                                typeId: getCardTypeId(mdeCards.cards[0].cardType)
                            })
                        }
                        else if (mdeCards.cards[1].cardNumber !== undefined) {
                            setCardValues({
                                ...cardValues,
                                code: mdeCards.cards[1].cardNumber,
                                tagDisplay: mdeCards.cards[1].cardNumber,
                                type: getCardTypeLabel(mdeCards.cards[1].cardType),
                                typeId: getCardTypeId(mdeCards.cards[1].cardType)
                            })
                        }
                        else {
                            toast.warn(translate.byKey(TranslationKeys.noCardFoundOnTheDevice));
                            setStep(3); //select card procedure step
                        }
                    }
                }
                // No card found
                else {
                    toast.warn(translate.byKey(TranslationKeys.noCardFoundOnTheDevice));
                    onLocalBack();
                }
            }
            else if (getEnrollResultCommandType(value) === enrollCmd.CMD_OMNIKEY) {
                const omnikeyCard = decodeOmnikeyPluginMsg(value) as IPluginCard;
                const seosType = 'Seos;'
                const code = parseInt(omnikeyCard.cardHex, 16).toString();

                if (omnikeyCard.cardNumber !== "0") {
                    setCardValues({
                        ...cardValues,
                        type: getCardTypeLabel(omnikeyCard.cardType),
                        code: code,
                        tagDisplay: code,
                        status: TagStatus.Normal.value,
                        typeId: getCardTypeId(omnikeyCard.cardType),
                        numberOfBits: omnikeyCard.formatType === seosType ? omnikeyCard.cardNoBits : STANDARD_NUMBER_OF_BITS,
                    })
                }
                else {
                   toast.warn(translate.byKey(TranslationKeys.noCardFoundOnTheDevice));
                    setStep(3); //select card procedure step
                }
            }
        }
    }

    /*
     User read a dual card on MDE or Omnikey.
     Show the portion of the card they chose to enroll.
    */
    const showDualCardPortion = (card: IPluginCard) => {
        setCardValues({
            ...cardValues,
            code: card.cardNumber,
            tagDisplay: card.cardNumber,
            type: getCardTypeLabel(card.cardType),
            typeId: getCardTypeId(card.cardType)
        })
    }

    const getCardType = (cardType: string) => {
        let type = getTagTypes().Slim; // Default to Slim for all other types.

        if (cardType === getTagTypes().Mifare.value) {
            type = getTagTypes().Mifare;
        }
        else if (cardType === getTagTypes().General.value) {
            type = getTagTypes().General;
        }
        else if (cardType === getTagTypes().HID.value) {
            type = getTagTypes().HID;
        }
        else if (cardType === getTagTypes().Felica.value) {
            type = getTagTypes().Felica;
        }
        else if (cardType === getTagTypes().WriTag_2048.value) {
            type = getTagTypes().WriTag_2048;
        }
        else if (cardType === getTagTypes().WriTag_128.value) {
            type = getTagTypes().WriTag_128;
        }

        return type;
    }

    const getCardTypeLabel = (cardType: string) => {
        return getCardType(cardType).label;
    }

    const getCardTypeId = (cardType: string) => {
        return getCardType(cardType).value;
    }

    useEffect(() => {
        if (pluginWSInstance && pluginWSInstance?.readyState === WebSocket.OPEN) {
            pluginWSInstance.onmessage = (value) => {
                onMessage(value.data)
            };
        }
    }, [step === 0]) // force useEffect to run when pluginWSInstance is instantiated, since pluginWSInstance is undefined on initial load.
                     // needed to update the card details detected when using MDE or Omnikey devices

    const requestToEnrol = (task) => {
        let command = '';
        const mdeDevice: PluginCommand = enrolmentDevice.MDE;
        const omnikeyDevice: PluginCommand = enrolmentDevice.OMNIKEY;

        switch (task) {
            case mdeDevice.value:
                command = !mdeDevice.command ? '' : mdeDevice.command;
                break;
            case omnikeyDevice.value:
                command = !omnikeyDevice.command ? '' : omnikeyDevice.command;
                break;
            default:
                break;
        }

        if (command !== '') {
            pluginWSInstance?.send(command); // set message to send
        }
    }

    useEffect(() => {
        if (supportedFields.length === 0) {
            setSideBarSteps(initialSteps);
        }
        else {
            const agField = supportedFields.some(data =>
                data.fields.some(field => field.columnId === settingTypesBasicFields.Access_Groups.id)
            );
            const credentialsField = supportedFields.some(data =>
                data.fields.some(field => field.columnId === settingTypesBasicFields.Tags.id)
            );

            const updatedSteps: WizardSideBarStep[] = [
                sideBarSteps[0],
                sideBarSteps[1],
                ...(credentialsField ? [{
                    title: translate.byKey(TranslationKeys.credential),
                    description: translate.byKey(TranslationKeys.pending),
                    substeps: []
                }] : []
                ),
                ...(agField ? [{
                    title: translate.byKey(TranslationKeys.accessGroups),
                    description: translate.byKey(TranslationKeys.pending),
                    substeps: []
                }] : []),
                ...(agField ? [{
                    title: translate.byKey(TranslationKeys.floorGroups),
                    description: translate.byKey(TranslationKeys.pending),
                    substeps: []
                }] : []),
                {
                    title: translate.byKey(TranslationKeys.summary),
                    description: translate.byKey(TranslationKeys.pending),
                    substeps: []
                },
            ];

            setAccessGroupField(agField);
            setTagsField(credentialsField);
            setSideBarSteps(updatedSteps);
        }
    }, [supportedFields]);

    const addSubstep = (stepIndex: number, substepTitle: string, updateMainStepDescription?: string) => {
        const updatedSteps = [...sideBarSteps];

        const sbStepExist = updatedSteps[stepIndex].substeps.some(substep => substep.title === substepTitle);
        if (!sbStepExist) {
            updatedSteps[stepIndex] = {
                ...updatedSteps[stepIndex],
                ...(updateMainStepDescription && {description: updateMainStepDescription}),
                substeps: [
                    ...updatedSteps[stepIndex].substeps,
                    { title: substepTitle }
                ]
            };

            setSideBarSteps(updatedSteps);
        }
    };

    const onLocalNext = (event: any) => {
        const nextStep = step + 1;

        if (nextStep === 2 && tagsField) {
            addSubstep(2, translate.byKey(TranslationKeys.task));
        }
        else if (event.task === enrolmentTask.ENROL_CARD.value) {
            addSubstep(2, translate.byKey(TranslationKeys.procedure), event.description);
            setCredentialType(event.description);
        }
        else if (event.task && (event.task === enrolmentDevice.MDE.value || event.task === enrolmentDevice.OMNIKEY.value)) {
            addSubstep(2, translate.byKey(TranslationKeys.details));
            setTaskProcedure(event.description);
            requestToEnrol(event.task);
        }
        else if (event.task === enrolmentDevice.MANUAL_CODE_TYPE.value || event.task === enrolmentDevice.RANDOM_PAC.value) {
            addSubstep(2, translate.byKey(TranslationKeys.details));
            setTaskProcedure(event.description);

            if (event.task && event.task === enrolmentDevice.RANDOM_PAC.value) {
                const PAC_TAG_TYPE_ID: string = "7";                
                const MAX_TAG_CODE: number = 99999;
                const code = (Math.floor(Math.random() * MAX_TAG_CODE) + 1).toString();

                setCardValues({
                    ...cardValues,
                    status: TagStatus.Normal.value,
                    typeId: PAC_TAG_TYPE_ID,                    
                    type: translate.byKey(TranslationKeys.personalAccessCode),
                    code: code,
                    tagDisplay: code,
                    numberOfBits: STANDARD_NUMBER_OF_BITS
                })
            }
            else if (event.task && event.task === enrolmentDevice.MANUAL_CODE_TYPE.value) {}
        }

        setStep(nextStep);
    };

    const onLocalBack = () => {
        if (step === 3 && tagsField && skip) { // credentials has been skipped, and substeps do not exist (have not been added or have been removed)
            setStep(step - 1);
            setSkip(false);
        }
        else if (step > 0) {
            setStep(step - 1);
        }
    };

    const onSkip = () => {
        if (sideBarSteps[2].substeps.length > 0) {
            const removeSubSteps = [...sideBarSteps];
            removeSubSteps[2] = {
                ...removeSubSteps[2],
                substeps: []
            };

            setSideBarSteps(removeSubSteps);
            setStep(step + 1)
        }
        else {
            setStep(step + 1)
        }
        setSkip(true);
    }

    const setSideBarStepDescription = (index, newDescription) => {
        const newSideBarSteps = sideBarSteps.map((step, stepIndex) => {
            return { ...step, description: stepIndex === index ? newDescription : step.description };
        });
        setSideBarSteps(newSideBarSteps);
    };

    const handleClose = () => {
        setStep(0);
        setSideBarSteps(initialSteps);
        setSupportedFields([]);
        setCardValues(initialValues);
        setOpen(false);
    };

    const buildMaster = (values): Person => ({
        profileId: values.categoryTypeId,
        name: values.name,
        selectedImage: values.selectedImage,
        email: values.email,
        uid: values.uid,
        pin: values.pin,
        enableCredentials: true,
        apbOverride: values.apbOverride,
        mobileNumber: values.mobileNumber,
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName,
        title: values.title,
        suffix: values.suffix,
        gender: values.gender,
        genderLabel: values.genderLabel,
        printTemplate: values.printTemplate,
        timeAndAttendance: values.timeAndAttendance,
        company: values.company,
        department: values.department,
        // newImage: values.newImage,
        siteId: siteId,
        // masterImages: values.masterImages,
        description: values.description,
        images: [],
        profileName: "",
        birthday: undefined,
        birthdayId: 0,
        id: 0,
        totalTags: 0,
        totalAccessGroups: 0,
        totalFloors: 0,
        totalAssets: 0,
        custom: values.custom
    });


    const buildCardInfo = (values: any): Tag => ({
        id: values.uid,
        code: values.code,
        statusId: values.statusId,
        expiryDate: values.expiryDate,
        expiryTime: values.expiryTime,
        startDate: values.startDate,
        startTime: values.startTime,
        typeId: values.typeId,
        type: values.type,
        tagDisplay: values.tagDisplay,
        codeUntruncated: values.code,
    });

    const storeDetails = async (values) => {
        try {

            const masterDto = buildMaster(values);
            const cardInfo = buildCardInfo(values);
            const toBeAssignedAccessGroups = values.accessGroupsIds;
            const toBeAssignedFloorGroups = values.floorGroupsIds;

            // Save master
            const masterData = (await storePerson(masterDto));

            // Save card info
            if (tagsField && values.code) {
                cardInfo.masterId = masterData.id;
                await saveCredential(cardInfo);
            }

            //get a default access group and remove it as it has already been added
            if (accessGroupField) {
                const masterGroup = (await getAccessGroupsForPerson(translate, { filters: appendFilter(masterData.id, 'Master', 'master.id', []) })).rows;
                const selectedIds = masterGroup.map(accessGroup => accessGroup.id.toString())

                if (toBeAssignedAccessGroups?.length > 0) {
                    const toBeAdded = toBeAssignedAccessGroups.filter((value: string) => !selectedIds.includes(value));
                    await assignUnassignMasterGroup(toBeAdded, [], masterData.id);
                }

                if (toBeAssignedFloorGroups?.length > 0) {
                    const toBeAdded = toBeAssignedFloorGroups.filter((value: string) => !selectedIds.includes(value));
                    await assignUnassignMasterGroup(toBeAdded, [], masterData.id);
                }
            }

            toast.success(translate.byKey(TranslationKeys.success));
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            handleClose();
        }
    }

    const getContent = () => {
        return [
            <TypeSelectionStep
                onBack={onLocalBack}
                onNext={onLocalNext}
                setSideBarStepDescription={newDescription => setSideBarStepDescription(step, newDescription)}
            />,
            <UserDetailsStep
                onBack={onLocalBack}
                onNext={onLocalNext}
                setSideBarStepDescription={newDescription => setSideBarStepDescription(step, newDescription)}
                supportedFields={supportedFields}
                setSupportedFields={setSupportedFields}
            />,
            ...(tagsField ? [
                <TasksForm
                    pluginDevice={pluginsAvailable}
                    availableSystemDeviceIds={availableSystemDeviceIds}
                    onNext={onLocalNext}
                    onBack={onLocalBack}
                    onSkip={onSkip}
                    taskIdsToExclude={[2,3,4,5,6,7]}
                    />,
            ] : []),
            ...(tagsField && !skip ? [
                <SubTasksForm
                    onNext={(event) => onLocalNext(event)}
                    pluginsAvailable={pluginsAvailable}
                    onBack={onLocalBack}
                    enrollMethod={enrolmentTask.ENROL_CARD.value}
                    availableSystemDeviceIds={availableSystemDeviceIds} />,
            ] : []),
            ...(tagsField && !skip ? [
                <DetailsForm
                    onNext={(event) => onLocalNext(event)}
                    onBack={onLocalBack}
                    cardInfo={cardValues} />,
            ] : []),
            ...(accessGroupField ? [
            <GroupStep
                key={"accessGroupStep"}
                onBack={onLocalBack}
                onNext={onLocalNext}
                setSideBarStepDescription={newDescription => setSideBarStepDescription(sideBarSteps.length - 3, newDescription)}
                groupType={1}/>,
            <GroupStep
                key={"floorGropStep"}
                onBack={onLocalBack}
                onNext={onLocalNext}
                setSideBarStepDescription={newDescription => setSideBarStepDescription(sideBarSteps.length - 2, newDescription)}
                groupType={2}/>
            ] : []),
            <QuickEnrolSummary
                onBack={onLocalBack}
                setSideBarStepDescription={newDescription => setSideBarStepDescription(step, newDescription)}
                credentialType={credentialType}
                taskProcedure={taskProcedure}/>
        ];
    }

    const onEditWidgetNext = () => {
        setStep(step + 1);
    }

    const onEditWidgetBack = () => {
        setStep(step - 1);
    }

    const onSave = (values: Widget) => {
        const titleForegroundColorId = values.settingDatasRefs?.find((item: any) => item.eSysname === TITLE_FGCOLOR)?.id;
        const titleBackgroundColorId = values.settingDatasRefs?.find((item: any) => item.eSysname === TITLE_BKGCOLOR)?.id;

        const settingDataToEdit: SettingData[] = [
            {
                sdValstring: values.titleForegroundColor,
                id: titleForegroundColorId
            },
            {
                sdValstring: values.titleBackgroundColor,
                id: titleBackgroundColorId
            }
        ];

        Promise.all(settingDataToEdit.map((settingData) => {
            return saveSettingData(settingData);
        })).then(() => {
            dashboard.onEditReload(values);
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            onClose();
        });
    }

    const editModalcontent: ReactElement[] = [
        <ColorStep onNext={onEditWidgetNext} onBack={onEditWidgetBack} />,
        <SummaryStep onBack={onEditWidgetBack}/>
    ];

    const steps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.widgetConfig),
            description: '',
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const onEdit = () => {
        dashboard.setIsDraggable(false);
        setIsOpen(true);
    };

    const onClose = () => {
        dashboard.setIsDraggable(true);
        setIsOpen(false);
        setStep(0);
    }

    return (
        <div className={`${previewMode ? 'w-32' : ''}`} style={{ width: '102.5px'}}>
            <WSPluginService
                onMessage={onMessageReceived}
                onOpen={onWebSocketOpen}
                onError={onWebSocketError}
                onClosed={onWebSocketClosed}
                url={window.location.protocol === "https:" ? WS_URL.SECURED_PLUGIN_MANAGER_WEBSOCKET : WS_URL.UNSECURED_PLUGIN_MANAGER_WEBSOCKET}/>
            {/*<div className={`${editMode ? 'w-32' : ''}`}>*/}
            <div>
                <BaseWidget
                    title={translate.byKey(TranslationKeys.enrol)}
                    titleBackgroundColor={widget.titleBackgroundColor}
                    titleForegroundColor={widget.titleForegroundColor}
                    onEdit={onEdit}
                    widgetKey={widget.id.toString()}
                    previewMode={previewMode}>
                    <div className="bg-darker cursor-pointer" style={{ width: "100%", height: "70px" }}>
                        <button disabled={editMode} className='flex flex-wrap w-full h-full justify-center content-center' type='button' onClick={() => { dashboard.setIsDraggable(false); setOpen(true) }}>
                            <img src={quickEnrollIcon} alt="Quick Enrol Icon" style={{ height: '60px' }} />
                        </button>
                    </div>
                </BaseWidget>
                <Modal
                    isOpen={open}
                    ariaHideApp={false}
                    overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
                    className="bg-white border-none m-auto shadow select-none outline-none w-content">
                    <Formik
                        enableReinitialize={true}
                        initialValues={initialValues}
                        onSubmit={storeDetails}>
                        <Form>
                            <WizardTwo
                                onClose={closeWizard}
                                step={step}
                                name={translate.byKey(TranslationKeys.quickEnrol)}
                                sidebarSteps={sideBarSteps}
                                stepContent={getContent()}/>
                        </Form>
                    </Formik>
                </Modal>
            </div>
            <EditWidgetModal
                isOpen={isOpen}
                onClose={onClose}
                steps={steps}
                content={editModalcontent}
                step={step}
                title={translate.byKey(TranslationKeys.quickEnrol)}
                widget={widget}
                onSave={onSave} />
        </div>
    );
};

export default QuickEnrolWidget;
